import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Forbidden: admin access required");
}

export type InterviewStatus = "pending" | "approved" | "refused" | "admin_processing";

export type UpcomingInterview = {
  paymentId: string;
  name: string;
  phone: string;
  email: string | null;
  consulate: string | null;
  interviewDate: string; // ISO date
  plan: string;
  interviewStatus: InterviewStatus;
  createdAt: string | null;
};

export const listUpcomingInterviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);

    // 1. Fetch ALL paid payments across past, yesterday, today, and upcoming dates
    const { data: pays, error } = await context.supabase
      .from("payments")
      .select("id, buyer_name, buyer_phone, buyer_email, plan_name, interview_date, interview_status, created_at")
      .eq("status", "paid")
      .not("interview_date", "is", null)
      .order("interview_date", { ascending: false })
      .limit(1000);
    if (error) throw new Error(error.message);

    // 2. Fetch known approvals from visa_approvals table to match by email or name
    const { data: visaApprovals } = await context.supabase
      .from("visa_approvals")
      .select("student_email, student_name, status")
      .eq("status", "approved");

    const approvedEmails = new Set(
      (visaApprovals ?? []).map((v: any) => (v.student_email || "").toLowerCase().trim()).filter(Boolean),
    );
    const approvedNames = new Set(
      (visaApprovals ?? []).map((v: any) => (v.student_name || "").toLowerCase().trim()).filter(Boolean),
    );

    // 3. Fetch client intakes to resolve consulate AND auto-approve matching names
    const emails = Array.from(
      new Set((pays ?? []).map((p: any) => (p.buyer_email || "").toLowerCase().trim()).filter(Boolean)),
    );
    const consulateByEmail = new Map<string, string>();
    const intakeApprovedEmails = new Set<string>();

    if (emails.length) {
      const { data: intakes } = await context.supabase
        .from("client_intakes")
        .select("email, full_name, visa_city, visa_outcome, status")
        .in("email", emails);

      (intakes ?? []).forEach((i: any) => {
        const em = (i.email || "").toLowerCase().trim();
        if (em) {
          if (i.visa_city) consulateByEmail.set(em, i.visa_city);
          // Check if marked approved in intake record
          if (i.visa_outcome?.toLowerCase().includes("approved") || i.status?.toLowerCase().includes("approved")) {
            intakeApprovedEmails.add(em);
          }
        }
      });
    }

    // 4. Group by candidate — keep the most recent interview record (including yesterday)
    const bestByKey = new Map<string, UpcomingInterview>();

    (pays ?? []).forEach((p: any) => {
      if (!p) return;
      const emailKey = String(p.buyer_email ?? "").toLowerCase().trim();
      const nameKey = String(p.buyer_name ?? "").toLowerCase().trim();
      const phoneKey = String(p.buyer_phone ?? "").replace(/\D/g, "");
      const key = emailKey || phoneKey || nameKey || p.id;

      let status = (p.interview_status ?? "pending") as InterviewStatus;

      // Auto-approve if name or email is already marked approved anywhere in intakes / approvals
      if (
        status === "pending" &&
        (approvedEmails.has(emailKey) || approvedNames.has(nameKey) || intakeApprovedEmails.has(emailKey))
      ) {
        status = "approved";
      }

      const candidate: UpcomingInterview = {
        paymentId: p.id,
        name: p.buyer_name || "Student",
        phone: p.buyer_phone || "",
        email: p.buyer_email || null,
        consulate: ((emailKey && consulateByEmail.get(emailKey)) || "").trim() || null,
        interviewDate: typeof p.interview_date === "string" ? p.interview_date : "",
        plan: p.plan_name || "Support Plan",
        interviewStatus: status,
        createdAt: typeof p.created_at === "string" ? p.created_at : null,
      };

      if (!candidate.interviewDate) return;
      const existing = bestByKey.get(key);
      // Keep the candidate record with the most recent interview date
      if (!existing || candidate.interviewDate > existing.interviewDate) {
        bestByKey.set(key, candidate);
      }
    });

    return {
      interviews: Array.from(bestByKey.values()).sort((a, b) => b.interviewDate.localeCompare(a.interviewDate)),
    };
  });

const VALID_STATUSES: InterviewStatus[] = ["pending", "approved", "refused", "admin_processing"];

/** Set (or clear) the visa interview date on a paid payment record. */
export const updateInterviewDate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { paymentId: string; interviewDate: string | null }) => {
    if (!data?.paymentId) throw new Error("paymentId required");
    if (data.interviewDate != null && !/^\d{4}-\d{2}-\d{2}$/.test(data.interviewDate)) {
      throw new Error("interviewDate must be YYYY-MM-DD");
    }
    return data;
  })
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("payments")
      .update({ interview_date: data.interviewDate })
      .eq("id", data.paymentId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const updateInterviewStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { paymentId: string; status: InterviewStatus }) => {
    if (!data?.paymentId) throw new Error("paymentId required");
    if (!VALID_STATUSES.includes(data.status)) throw new Error("invalid status");
    return data;
  })
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);

    const { data: pay, error: payErr } = await context.supabase
      .from("payments")
      .select("id, buyer_name, buyer_phone, buyer_email, plan_name, interview_date")
      .eq("id", data.paymentId)
      .maybeSingle();
    if (payErr) throw new Error(payErr.message);
    if (!pay) throw new Error("Payment not found");

    const { error: upErr } = await context.supabase
      .from("payments")
      .update({ interview_status: data.status })
      .eq("id", data.paymentId);
    if (upErr) throw new Error(upErr.message);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = (pay.buyer_email ?? "").toLowerCase().trim();

    if (data.status === "approved") {
      const approvedAtTimestamp = pay.interview_date
        ? new Date(`${pay.interview_date}T12:00:00Z`).toISOString()
        : new Date().toISOString();

      await supabaseAdmin.from("visa_approvals").upsert(
        {
          student_email: email || `${pay.id}@placeholder.com`,
          student_name: pay.buyer_name ?? "Student",
          whatsapp: pay.buyer_phone ?? null,
          plan: pay.plan_name ?? null,
          visa_date: pay.interview_date ?? null,
          status: "approved",
          approved_at: approvedAtTimestamp,
        } as never,
        { onConflict: "student_email" as never },
      );
    } else {
      if (email) {
        await supabaseAdmin.from("visa_approvals").update({ status: "revoked" }).eq("student_email", email);
      }
    }

    return { ok: true };
  });
